import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export enum SortOrder {
  'Ascending' = 'Ascending',
  'Descending' = 'Descending',
}

export enum StatusEnum {
  'ACTIVE' = 'ACTIVE',
  'ARCHIVED' = 'ARCHIVED',
  'DELETED' = 'DELETED',
}

export enum FilterOperator {
  'And' = 'And',
  'Or' = 'Or',
}

export enum Comparator {
  'Eq' = 'Eq',
  'Gt' = 'Gt',
  'Lt' = 'Lt',
  'Like' = 'Like',
  'GtEq' = 'GtEq',
  'LtEq' = 'LtEq',
  'NEq' = 'NEq',
  'ILike' = 'ILike',
}

export interface Filter {
  field?: string;
  value?: string;
  action?: Comparator;
}

export interface FilterSet {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: SortOrder;
  filterOperator?: FilterOperator;
  filterFilters?: Filter[];
  filterFilterGroups?: FilterGroup[];
}

export interface FilterGroup {
  operator?: FilterOperator;
  filters?: Filter[];
  filterGroups?: FilterGroup[];
}

export function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

export function enumKeys<T extends object>(e: T) {
  const keys = Object.keys(e);
  const isStringEnum = isNaN(Number(keys[0]));
  return isStringEnum ? keys : keys.slice(keys.length / 2);
}

export function createHumanList<T>(list: T[]): string {
  const last = list.pop();
  return list.join(', ') + (list.length ? ' and ' : '') + last;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RecordValueType<A> = A extends Record<any, infer V> ? V : never;

/** Adds an s if count !== 1 (does not handle fancy plurals) */
export const plural = (singular: string, count = 0) => {
  return singular + (count === 1 ? '' : 's');
};

/**
 * Parses a path-like string and returns the last segment split into a label and numeric suffix.
 * Examples:
 *  - "__ihub/01.../Dahua-1" => { label: "Dahua", number: 1 }
 *  - "foo/bar/Baz-12"       => { label: "Baz", number: 12 }
 *  - "foo/bar/Camera"       => { label: "Camera" }
 *  - "foo/bar/Device-abc"   => { label: "Device-abc" } // non-numeric suffix is treated as part of label
 */
export function parseLabelAndNumberFromPath(input: string): { label: string; number?: number } {
  // Split on the LAST hyphen in the entire input and ensure the suffix is numeric
  const hyphenIndex = input.lastIndexOf('-');
  if (hyphenIndex > 0) {
    const label = input.slice(0, hyphenIndex);
    const suffix = input.slice(hyphenIndex + 1);
    if (/^\d+$/.test(suffix)) {
      return { label, number: parseInt(suffix, 10) };
    }
    // Non-numeric suffix: still return the name as everything before the last hyphen
    return { label };
  }

  return { label: input };
}

export enum AppEnvironment {
  Production = 'production',
  Staging = 'staging',
  Local = 'local',
}

/**
 * Determines the current application environment based on VITE_ADMIN_API
 */
export function getAppEnvironment(): AppEnvironment {
  const apiUrl = import.meta.env.VITE_ADMIN_API ?? '';

  if (apiUrl.includes('api.admin.watchful')) {
    return AppEnvironment.Production;
  }
  if (apiUrl.includes('api.admin.test')) {
    return AppEnvironment.Staging;
  }
  if (apiUrl.includes('localhost')) {
    return AppEnvironment.Local;
  }

  // Default to Prod if no matches
  return AppEnvironment.Production;
}

